/**
 * Status → explanation / recovery for incomplete attachment extract.
 * Distinguishes text extracted vs content inspected. No HOLD bypass.
 */

export const EXTRACT_GUIDANCE = {
  empty: {
    summary: "Geen leesbare tekst gevonden",
    detail:
      "Er is geen extracteerbare tekstlaag. Tekstloze pagina's zijn niet automatisch scanpagina's. "
      + "Tekst geëxtraheerd is niet hetzelfde als inhoud geïnspecteerd.",
    recovery:
      "Verwijder deze bijlage en upload een leesbare versie met tekst, of een tekstbestand.",
    recoveryAction: "replace_readable",
  },
  truncated: {
    summary: "Tekst afgekapt door limiet",
    detail:
      "De extractielimiet is bereikt. Alleen een deel van de tekst is gelezen, dus volledige "
      + "inhoudsinspectie is niet mogelijk.",
    recovery:
      "Splits of verkort het bestand, verwijder deze bijlage en upload opnieuw. Daarna opnieuw versturen.",
    recoveryAction: "split_or_replace",
  },
  partial: {
    summary: "Document niet volledig leesbaar",
    detail:
      "Niet alle onderdelen leverden leesbare tekst. Tekstloze delen zijn niet automatisch scans. "
      + "Zonder volledige tekstextractie volgt geen volledige inspectie.",
    recovery:
      "Verwijder deze bijlage en upload een volledig leesbare versie, of splits het document.",
    recoveryAction: "replace_readable",
  },
  error: {
    summary: "Tekstextractie mislukt",
    detail:
      "De extractor kon dit bestand niet verwerken. Zonder tekst is inhoudsinspectie niet mogelijk.",
    recovery: "Verwijder deze bijlage en upload het bestand opnieuw, of een ander leesbaar formaat.",
    recoveryAction: "reupload",
  },
  unknown: {
    summary: "Extractiestatus onbekend",
    detail:
      "De extractiestatus ontbreekt of is ongeldig. Fail closed: geen volledige inspectie.",
    recovery: "Verwijder deze bijlage en upload het bestand opnieuw.",
    recoveryAction: "reupload",
  },
};

export function guidanceForStatus(status) {
  const key = status && EXTRACT_GUIDANCE[status] ? status : "unknown";
  return EXTRACT_GUIDANCE[key];
}

/** Short coverage line from count-only extract_coverage (no document body). */
export function formatExtractCoverage(coverage) {
  if (!coverage || typeof coverage !== "object") return null;
  const parts = [];
  const total = coverage.pages_total;
  const withText = coverage.pages_with_text;
  const without = coverage.pages_without_text;
  if (typeof total === "number" && typeof withText === "number") {
    // Never invent "zonder tekst" for unvisited pages (early stop → unknown).
    const bit = [`PDF pagina's: ${total} totaal`, `${withText} met tekst`];
    if (typeof without === "number") bit.push(`${without} zonder tekst`);
    const known = withText + (typeof without === "number" ? without : 0);
    if (known < total) bit.push(`${total - known} dekking onbekend`);
    parts.push(bit.join(", "));
  }
  if (coverage.limit_reached === true) {
    const max = typeof coverage.max_chars === "number" ? coverage.max_chars : null;
    parts.push(max != null ? `Extractielimiet bereikt (${max} tekens)` : "Extractielimiet bereikt");
  } else if (typeof coverage.chars_extracted === "number" && coverage.chars_extracted > 0) {
    parts.push(`${coverage.chars_extracted} tekens geëxtraheerd`);
  }
  return parts.length ? parts.join(". ") : null;
}

export function extractGuidanceForAttachment(att) {
  if (!att) return guidanceForStatus("unknown");
  const status =
    att.extract_status == null || att.extract_status === ""
      ? "unknown"
      : String(att.extract_status);
  if (status === "ok" && att.extract_complete === true) return null;
  const g = guidanceForStatus(status === "ok" ? "unknown" : status);
  const coverageLine = formatExtractCoverage(att.extract_coverage);
  return coverageLine ? { ...g, coverageLine } : g;
}
